import React, { useEffect, useRef, useState } from "react";
import "./MultiSelectSearchDropdown.css";
import MultiSelectRoundedItem from "./MultiSelectRoundedItem";
import { useTranslation } from "react-i18next";

const EMPTY_ARRAY = [];

const MultiSelectSearchDropdownGlobal = ({
  options = EMPTY_ARRAY,
  isOpen,
  onToggle,
  onChange,
  defaultValue = EMPTY_ARRAY,
  onSearch,
  label = "", // ✅ add
  important = false, // ✅ add
  error = false, // ✅ add
  className = "", // ✅ optional search callback
  readOnly = false,
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedItems, setSelectedItems] = useState(defaultValue);
  const { t } = useTranslation();
  const wrapperRef = useRef(null);

  /* ========================
     SYNC PROPS
  ======================== */

  useEffect(() => {
    setSelectedItems(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultValue)]);

  useEffect(() => {
    setFilteredOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(options)]);

  useEffect(() => {
    onChange && onChange(selectedItems);
  }, [selectedItems]);

  /* ========================
     CLOSE ON CLICK OUTSIDE
  ======================== */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        onToggle && onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  /* ========================
     HANDLERS
  ======================== */

  const handleSearch = (value) => {
    if (onSearch) {
      onSearch(value); // parent can call API
      return;
    }

    const filtered = options.filter((opt) =>
      opt.label.toLowerCase().includes(value.toLowerCase()),
    );

    setFilteredOptions(filtered);
  };

  const handleSelect = (value, label) => {
    setSelectedItems((prev) => {
      const exists = prev.some((item) => item.value === value);

      if (exists) {
        // ✅ UNSELECT
        return prev.filter((item) => item.value !== value);
      }

      // ✅ SELECT
      return [...prev, { value, label }];
    });
  };

  const removeSelection = (value) => {
    setSelectedItems((prev) => prev.filter((item) => item.value !== value));
  };

  /* ========================
     RENDER
  ======================== */

  return (
    <div className="multi-select-box-wrapper" ref={wrapperRef}>
      <div
        onClick={!readOnly ? onToggle : undefined}
        className={`multi-select-box-container ${className} ${
          error ? "multi-error" : ""
        } ${readOnly ? "readonly-view" : ""}`}
        tabIndex={0}
      >
        <div className="floating-label-wrapper">
          {/* LABEL */}
          <label
            className={`search-select-label common-text color-999 ftw-4 ${
              selectedItems.length > 0 ? "has-value" : ""
            }`}
          >
            {t(label)}
            {important && <span className="required-asterisk">*</span>}
          </label>

          {/* SELECTED ITEMS */}
          <div className="multi-selected-items-container">
            {selectedItems.map((item) => (
              <MultiSelectRoundedItem
                key={item.value}
                name={item.label}
                onclose={
                  !readOnly ? () => removeSelection(item.value) : undefined
                }
                hideClose={readOnly} // 👈 NEW PROP
              />
            ))}
          </div>
        </div>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="9"
          height="5"
          viewBox="0 0 9 5"
          fill="none"
          className={`multi-dropdown-arrow ${isOpen ? "open" : ""}`}
        >
          <path
            d="M0.583496 0.583313L4.0835 4.08331L7.5835 0.583313"
            stroke="#717182"
            strokeWidth="1.16667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="multi-expanded-container">
          <div className="multi-search-section">
            <input
              type="text"
              className="multi-search-input"
              placeholder={t("Search...")}
              onInput={(e) => handleSearch(e.target.value)}
            />
          </div>

          <ul>
            {filteredOptions.map((option) => (
              <li
                key={option.value}
                onClick={
                  !readOnly
                    ? () => handleSelect(option.value, option.label)
                    : undefined
                }
              >
                <input
                  type="checkbox"
                  checked={selectedItems.some(
                    (item) => item.value === option.value,
                  )}
                  readOnly
                />
                <span className="customer-val-fld">{option.value}</span>
                <span>{option.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectSearchDropdownGlobal;
